import { CSSProperties, FC, useMemo } from 'react';
import {
  Button,
  Icon,
  Radio,
  RadioGroup,
  Select,
  SelectOption,
  Slider,
  Stack,
  Tag,
  TextBody,
  TextCaption,
  Tooltip,
} from '@pypestream/design-system';

import { useAudioPlayback } from '../../../hooks/use-audio-playback';
import {
  entryLabel,
  localeAvailability,
  localeOptions,
  shortVoiceName,
  type LanguageCatalogEntry,
  type SpeechRecognition,
} from '../language-catalog';
import {
  languageKeyOf,
  withAccent,
  type LanguageEntry,
} from '../languages-config';
import type { LanguageEntryError } from '../validate-languages';
import {
  accentFromOptionValue,
  accentToOptionValue,
} from '../../modals/voice-selector-modal/voice-search-args';
import { useVoiceAiAccents } from './use-voice-ai-catalog';
import { VoicePreviewCard } from './voice-preview-card';

export type AudioModelOption = {
  value: string;
  label: string;
  description: string;
  recommended?: boolean;
};

const titleCase = (value: string): string =>
  value.replace(/(^|\s)\S/g, (char) => char.toUpperCase());

const errorMessage = (
  error: LanguageEntryError,
  label: string,
  speechModel: string
): string => {
  switch (error) {
    case 'no-voice':
      return 'Choose a voice for this language.';
    case 'no-audio-model':
      return 'Choose an audio model.';
    case 'unknown-locale':
      return 'This locale is not supported. Choose another language.';
    case 'duplicate-language':
      return 'This language is already on the channel.';
    case 'google-only':
      return `${label} needs Google speech recognition. Switch Speech Recognition to Google, or remove this language.`;
    case 'english-only-model':
      return `The ${speechModel} speech model is English only. Use flux or Google to add other languages.`;
  }
};

type SliderField =
  | 'elevenlabsSpeed'
  | 'elevenlabsStability'
  | 'elevenlabsSimilarity';

const SLIDERS: Array<{
  field: SliderField;
  label: string;
  tooltip: string;
  min: number;
  max: number;
  testId: string;
}> = [
  {
    field: 'elevenlabsSpeed',
    label: 'Speed',
    tooltip:
      'Controls the speed of speech. Higher values make it faster; lower values make it slower.',
    min: 0.7,
    max: 1.2,
    testId: 'speed-slider',
  },
  {
    field: 'elevenlabsStability',
    label: 'Stability',
    tooltip:
      'Controls the stability of the voice. Higher values make it more stable; lower values make it more variable.',
    min: 0,
    max: 1,
    testId: 'stability-slider',
  },
  {
    field: 'elevenlabsSimilarity',
    label: 'Similarity',
    tooltip:
      'Controls how similar the voice sounds to the original. Higher values make it more similar; lower values make it more different.',
    min: 0,
    max: 1,
    testId: 'similarity-slider',
  },
];

/*
 * Layout and type from the Figma "Pro Studio (Production)" Languages section
 * (node 8571:15655): a white 0.82 card with a Purple/100 border, 8px radius
 * and 16px padding; Subtitle 2 for the entry name, Caption for its meta.
 */
/** Error red at 5.2:1 on white (WCAG 1.4.3); the design's #ff4343 is 3.6:1. */
export const ERROR_RED = '#ce2c31';

/** Off-screen but read by assistive tech. */
const VISUALLY_HIDDEN: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
};

const CARD: CSSProperties = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  padding: 16,
  borderRadius: 8,
  border: '1px solid',
  borderColor: '#e7e3fa',
  background: 'rgba(255, 255, 255, 0.82)',
};

const HEADER_ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  cursor: 'pointer',
};

const HEADER_LEFT: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minWidth: 0,
};

/** The design's bare 24px chevron: no fill, no border. */
const CHEVRON_BUTTON: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  flexShrink: 0,
};

const BODY_ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'stretch',
  gap: 24,
};

const SETTINGS_COLUMN: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

const FIELD_ROW: CSSProperties = { display: 'flex', gap: 16 };

const FIELD: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const VOICE_FIELD: CSSProperties = {
  width: '100%',
  height: 40,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 8px 8px 12px',
  border: '1px solid rgba(98, 105, 124, 0.2)',
  borderRadius: 8,
  background: '#ffffff',
  cursor: 'pointer',
  font: 'inherit',
};

const VOICE_AVATAR: CSSProperties = {
  width: 24,
  height: 24,
  borderRadius: 100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#e7e3fa',
  color: '#8573e7',
  fontSize: 11,
  lineHeight: '16px',
};

const TEXT = {
  entryTitle: {
    fontSize: 14,
    lineHeight: '24px',
    fontWeight: 500,
    color: '#151a25',
    whiteSpace: 'nowrap',
  },
  entryMeta: {
    fontSize: 11,
    lineHeight: '16px',
    color: '#62697c',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  startingPill: {
    fontSize: 11,
    lineHeight: '16px',
    color: '#151a25',
    background: 'rgba(21, 26, 37, 0.05)',
    padding: '4px 8px',
    borderRadius: 100,
    whiteSpace: 'nowrap',
  },
  fieldValue: {
    fontSize: 14,
    lineHeight: '24px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
} satisfies Record<string, CSSProperties>;

export interface LanguageEntryCardProps {
  entry: LanguageEntry;
  isStarting: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  catalog: LanguageCatalogEntry[];
  speechRecognition: SpeechRecognition;
  /** Keys of the OTHER entries on the channel. */
  otherLanguages: string[];
  errors: LanguageEntryError[];
  audioModels: AudioModelOption[];
  canRemove: boolean;
  onChange: (patch: Partial<LanguageEntry>) => void;
  onMakeStarting: () => void;
  onRemove: () => void;
  onChooseVoice: () => void;
}

/**
 * One language of the Languages block. The voice settings mirror the old
 * single Voice section, scoped to this language; speech recognition is not
 * here because it is channel-wide.
 */
export const LanguageEntryCard: FC<LanguageEntryCardProps> = ({
  entry,
  isStarting,
  open,
  onOpenChange,
  catalog,
  speechRecognition,
  otherLanguages,
  errors,
  audioModels,
  canRemove,
  onChange,
  onMakeStarting,
  onRemove,
  onChooseVoice,
}) => {
  const { data: accents, isLoading: accentsLoading } = useVoiceAiAccents(
    entry.key
  );
  const { playAudio, isPlaying } = useAudioPlayback();
  const voice = entry.elevenlabsVoiceMetadata;

  // Shown accent: the one picked as a filter, else the chosen voice's own.
  // withAccent keeps the two from disagreeing while a voice is set.
  const shownAccent = entry.accent || voice?.accent || undefined;
  const label = entryLabel(
    { locale: entry.locale, accent: shownAccent },
    catalog
  );
  const options = useMemo(() => localeOptions(catalog), [catalog]);
  const testId = (suffix: string) => `ps-voice-language-${entry.key}-${suffix}`;
  const hasErrors = errors.length > 0;
  const errorId = testId('errors');
  const titleId = testId('title');
  const bodyId = testId('body');

  // The saved locale must stay selectable even if the catalog dropped it,
  // or the select would render blank while the entry still holds it.
  const localeKnown = options.some((o) => o.locale === entry.locale);

  const toggle = () => onOpenChange(!open);

  return (
    <div
      {...{ 'test-id': testId('item') }}
      data-open={open ? 'true' : 'false'}
      style={{
        ...CARD,
        borderColor: hasErrors ? ERROR_RED : CARD.borderColor,
      }}
    >
      {/* The whole row toggles for the mouse; the chevron is the control
          keyboard and assistive tech use (a button with aria-expanded). */}
      <div
        {...{ 'test-id': testId('header') }}
        onClick={toggle}
        style={HEADER_ROW}
      >
        <div style={HEADER_LEFT}>
          <span id={titleId} style={TEXT.entryTitle}>
            {label}
          </span>
          {hasErrors && (
            <>
              <Icon
                name="warning"
                color="red"
                size="xsmall"
                aria-hidden="true"
              />
              {/* Said in words: a collapsed entry shows only the icon. */}
              <span style={VISUALLY_HIDDEN}>needs attention</span>
            </>
          )}
          {open ? (
            // Picking the starting language must not toggle the entry.
            <div onClick={(e) => e.stopPropagation()}>
              <RadioGroup
                size="small"
                aria-label={`Starting language: ${label}`}
                value={isStarting ? entry.key : ''}
                onChange={() => onMakeStarting()}
                test-id={testId('starting-radio')}
              >
                <Radio value={entry.key}>Starting language</Radio>
              </RadioGroup>
            </div>
          ) : (
            <>
              <span style={TEXT.entryMeta}>
                {[
                  entry.elevenlabsAudioModel,
                  voice?.name ? shortVoiceName(voice.name) : undefined,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
              {isStarting && (
                <span
                  {...{ 'test-id': testId('starting-tag') }}
                  style={TEXT.startingPill}
                >
                  Starting language
                </span>
              )}
            </>
          )}
        </div>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          // aria-expanded carries the state; a label that also flips would
          // announce it twice.
          aria-label={`${label} settings`}
          {...{ 'test-id': testId('toggle') }}
          style={CHEVRON_BUTTON}
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
        >
          <Icon
            name="chevron-down"
            size="small"
            weight="medium"
            color="gray"
            style={{
              transform: open ? 'rotate(180deg)' : undefined,
              transition: 'transform 150ms ease',
            }}
          />
        </button>
      </div>

      {open && (
        <div
          id={bodyId}
          role="group"
          aria-labelledby={titleId}
          aria-describedby={hasErrors ? errorId : undefined}
          style={BODY_ROW}
        >
          <div style={SETTINGS_COLUMN}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={FIELD_ROW}>
                <div style={FIELD}>
                  <TextBody size="small">Language</TextBody>
                  <Select
                    value={entry.locale}
                    variant="outlined"
                    placeholder="Select Language"
                    aria-label={`Language, ${label}`}
                    test-id={testId('locale-select')}
                    onChange={(e) => {
                      const locale = e.target.value as string;
                      if (locale === entry.locale) return;
                      const sameLanguage = languageKeyOf(locale) === entry.key;
                      // Another region of the same language keeps its voice;
                      // another language does not — the voice was searched
                      // for the old one.
                      onChange(
                        sameLanguage
                          ? { locale }
                          : {
                              locale,
                              key: languageKeyOf(locale),
                              accent: undefined,
                              elevenlabsVoiceId: '',
                              elevenlabsVoiceMetadata: undefined,
                            }
                      );
                    }}
                  >
                    {!localeKnown && (
                      <SelectOption value={entry.locale}>
                        {entry.locale}
                      </SelectOption>
                    )}
                    {options.map((option) => {
                      const availability = localeAvailability(
                        option,
                        speechRecognition,
                        otherLanguages
                      );
                      return (
                        <SelectOption
                          key={option.locale}
                          value={option.locale}
                          disabled={!availability.enabled}
                        >
                          {option.label}
                          {option.googleOnly && (
                            <Tag
                              slot="suffix"
                              size="small"
                              variant="default"
                              minimal
                            >
                              Google only
                            </Tag>
                          )}
                        </SelectOption>
                      );
                    })}
                  </Select>
                </div>
                <div style={FIELD}>
                  <Stack alignItems="center" gutter="2xsmall">
                    <TextBody size="small">Accent (Optional)</TextBody>
                    <Tooltip
                      placement="right"
                      content="Narrows the voices to one accent. Leave it empty to choose from every voice of the language."
                    >
                      <Icon name="info" color="gray" size="xsmall" />
                    </Tooltip>
                  </Stack>
                  <Select
                    value={
                      entry.accent ? accentToOptionValue(entry.accent) : ''
                    }
                    variant="outlined"
                    placeholder={
                      accentsLoading ? 'Loading accents…' : 'Select Accent'
                    }
                    disabled={accentsLoading || accents.length === 0}
                    aria-label={`Accent, ${label}`}
                    test-id={testId('accent-select')}
                    onChange={(e) => {
                      const accent = accentFromOptionValue(
                        e.target.value as string
                      );
                      if (accent !== entry.accent)
                        onChange(withAccent(entry, accent));
                    }}
                  >
                    <SelectOption value="">Any accent</SelectOption>
                    {entry.accent && !accents.includes(entry.accent) && (
                      <SelectOption value={accentToOptionValue(entry.accent)}>
                        {titleCase(entry.accent)}
                      </SelectOption>
                    )}
                    {accents.map((accent) => (
                      <SelectOption
                        key={accent}
                        value={accentToOptionValue(accent)}
                      >
                        {titleCase(accent)}
                      </SelectOption>
                    ))}
                  </Select>
                </div>
              </div>

              <TextBody size="small" style={{ fontWeight: 500 }}>
                Voice
              </TextBody>
            </div>

            <div style={FIELD_ROW}>
              <div style={FIELD}>
                <TextBody size="small">Audio Model</TextBody>
                <Select
                  value={entry.elevenlabsAudioModel}
                  variant="outlined"
                  placeholder="Select Model"
                  aria-label={`Audio model, ${label}`}
                  test-id={testId('audio-model-select')}
                  onChange={(e) =>
                    onChange({
                      elevenlabsAudioModel: e.target.value as string,
                    })
                  }
                >
                  {audioModels.map((model) => (
                    <SelectOption
                      key={model.value}
                      value={model.value}
                      description={model.description}
                    >
                      {model.label}
                      {model.recommended && (
                        <Tag
                          slot="suffix"
                          size="small"
                          newVariant="filled"
                          variant="default"
                          minimal
                        >
                          Recommended
                        </Tag>
                      )}
                    </SelectOption>
                  ))}
                </Select>
              </div>
              <div style={FIELD}>
                <TextBody size="small">Voice</TextBody>
                <button
                  type="button"
                  onClick={onChooseVoice}
                  disabled={!entry.elevenlabsAudioModel}
                  {...{ 'test-id': testId('voice-button') }}
                  // Names start with the visible text (WCAG 2.5.3).
                  aria-label={
                    voice?.name
                      ? `${shortVoiceName(voice.name)}, change voice`
                      : 'Select Voice'
                  }
                  style={VOICE_FIELD}
                >
                  <span
                    style={{ display: 'flex', alignItems: 'center', gap: 10 }}
                  >
                    {voice?.name && (
                      <span aria-hidden="true" style={VOICE_AVATAR}>
                        {voice.name.substring(0, 1).toUpperCase()}
                      </span>
                    )}
                    <span
                      style={{
                        ...TEXT.fieldValue,
                        color: voice?.name ? '#151a25' : '#a1a5b0',
                      }}
                    >
                      {voice?.name
                        ? shortVoiceName(voice.name)
                        : 'Select Voice'}
                    </span>
                  </span>
                  <Icon name="switch" size="small" aria-hidden="true" />
                </button>
              </div>
            </div>

            {SLIDERS.map((slider) => (
              <div
                key={slider.field}
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                <Stack justifyContent="space-between" alignItems="center">
                  <Stack alignItems="center" gutter="2xsmall">
                    <TextBody size="small">{slider.label}</TextBody>
                    <Tooltip placement="right" content={slider.tooltip}>
                      <Icon name="info" color="gray" size="xsmall" />
                    </Tooltip>
                  </Stack>
                  <TextBody size="small">{entry[slider.field]}</TextBody>
                </Stack>
                <Slider
                  step={0.1}
                  min={slider.min}
                  max={slider.max}
                  value={entry[slider.field]}
                  tooltip="top"
                  showMinMaxLabels={false}
                  aria-label={`${slider.label}, ${label}`}
                  test-id={testId(slider.testId)}
                  // @ts-expect-error - same untyped onInput as the form's sliders
                  onInput={(e) =>
                    onChange({ [slider.field]: parseFloat(e.target.value) })
                  }
                />
              </div>
            ))}

            {hasErrors && (
              <div id={errorId} role="alert" {...{ 'test-id': errorId }}>
                {errors.map((error) => (
                  <TextCaption key={error} style={{ color: ERROR_RED }}>
                    {errorMessage(error, label, speechRecognition.speechModel)}
                  </TextCaption>
                ))}
              </div>
            )}

            {canRemove && (
              <div style={{ marginTop: 'auto', paddingTop: 8 }}>
                <Button
                  variant="danger"
                  size="large"
                  onClick={onRemove}
                  test-id={testId('remove-button')}
                >
                  Remove Language
                </Button>
              </div>
            )}
          </div>

          {voice?.name && (
            <VoicePreviewCard
              testId={testId('preview')}
              voiceName={shortVoiceName(voice.name)}
              languageName={label.split(' (')[0]}
              accent={voice.accent || undefined}
              isPlaying={isPlaying(entry.key)}
              canPlay={!!voice.preview_url}
              onTogglePlay={() => {
                if (voice.preview_url) playAudio(entry.key, voice.preview_url);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};
